/**
 * DispatchRoutesView — Phase 2 of route planning.
 *
 * Lets the operator:
 *  1. See a list of saved delivery routes grouped by status (Pending vs Planned vs Completed)
 *  2. Create a new route by picking start + end warehouses, then multi-selecting
 *     approved orders to include
 *  3. Drag-and-drop reorder stops manually (Phase 2 — no auto-optimize yet)
 *  4. Edit/delete a route, change status
 *
 * Phase 3 will add an "Optimize" button that calls Google Routes API and
 * fills in distance/duration + auto-orders stops by hotel opening hours.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  Route as RouteIcon, Plus, Trash2, Edit2, GripVertical, MapPin, Truck, Calendar,
  Warehouse as WarehouseIcon, ChevronRight, Clock, User, Car, Printer, Eye, CheckCircle2, AlertCircle,
  PackageOpen, X,
} from "lucide-react";
import RunPackingBoard, { PackStopModal } from "./RunPackingBoard";
import DispatchHistory from "./DispatchHistory";
import { printHtmlViaIframe } from "@/lib/printHtml";

import AddressAutocomplete from "@/components/AddressAutocomplete";
import QRCode from "qrcode";
import CombinedPickupPackingListModal from "./CombinedPickupPackingListModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const STATUS_LABEL = {
  pending: { text: "Pending", className: "bg-amber-100 text-amber-800 border-amber-300" },
  planned: { text: "Planned", className: "bg-blue-100 text-blue-800 border-blue-300" },
  completed: { text: "Completed", className: "bg-green-100 text-green-800 border-green-300" },
};

const todayIsoLocal = () => {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtMeters = (m) => (m == null ? "—" : m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`);
const fmtSeconds = (s) => {
  if (s == null) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// Unique sortable id helper — works for both order stops and custom stops.
const stopId = (s) => (s.kind === "custom" ? `c:${s.id}` : `o:${s.order_id || s.id}`);

// ────────────────────────────────────────────────────────────────────────────
// Sortable stop row
// ────────────────────────────────────────────────────────────────────────────
function SortableStopRow({ stop, index, onRemove, eta, legDistanceM }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: stopId(stop) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Stop refers to a deleted order — render a minimal "broken" row with a
  // remove button so the operator can prune it from the route.
  if (stop.missing) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center gap-2 bg-red-50 border border-red-300 rounded-md p-2"
        data-testid={`route-stop-missing-${stopId(stop)}`}
      >
        <span className="font-mono font-bold text-red-700 w-7 text-center">{index + 1}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-red-800">Stop no longer exists (deleted)</p>
          <p className="text-xs text-red-700 font-mono">{stopId(stop)}</p>
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="text-red-700 hover:bg-red-100"
          onClick={() => onRemove(stop)}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  }

  const isCustom = stop.kind === "custom";
  const title = isCustom ? stop.label : stop.hotel_name;
  const subtitle = isCustom
    ? (stop.address || "Custom stop — no address")
    : `${stop.order_number || ""}${stop.address ? ` · ${stop.address}` : ""} · ${stop.items_count} pcs`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 border rounded-md p-2 hover:shadow-sm ${isCustom ? "bg-violet-50 border-violet-200" : "bg-white"}`}
      data-testid={`route-stop-${stopId(stop)}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-neutral-400 hover:text-neutral-700 touch-none"
        aria-label="Drag to reorder"
        data-testid={`route-stop-drag-${stopId(stop)}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span className="font-mono font-bold text-brand-navy w-7 text-center">{index + 1}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold truncate">{title || "—"}</span>
          {isCustom && (
            <Badge className="bg-violet-100 text-violet-800 border-violet-300 text-[10px]">
              Custom stop
            </Badge>
          )}
          {!isCustom && stop.is_pickup && (
            <Badge className="bg-orange-100 text-orange-800 border-orange-300 text-[10px]">
              <Truck className="w-3 h-3 mr-1" />Pickup
            </Badge>
          )}
          {stop.lat != null && stop.lng != null ? (
            <Badge variant="outline" className="text-green-700 border-green-300 text-[10px]">
              <MapPin className="w-3 h-3 mr-0.5" />Geocoded
            </Badge>
          ) : (
            <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]" title="Without coordinates this stop is skipped during route optimization.">
              No coords
            </Badge>
          )}
          {eta && (
            <Badge variant="outline" className="text-blue-700 border-blue-300 text-[10px]">
              <Clock className="w-3 h-3 mr-0.5" />ETA {eta}
            </Badge>
          )}
          {legDistanceM != null && index > 0 && (
            <span className="text-[10px] text-muted-foreground">+{(legDistanceM / 1000).toFixed(1)} km from prev</span>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
        {!isCustom && stop.special_note && (
          <div className="mt-1 text-[11px] text-amber-900 bg-amber-100 border-l-2 border-amber-500 pl-2 py-0.5 rounded-r whitespace-pre-wrap break-words" data-testid={`route-stop-special-note-${stopId(stop)}`}>
            <span className="font-bold">Note:</span> {stop.special_note}
          </div>
        )}
      </div>
      <Button
        size="icon"
        variant="ghost"
        className="text-red-700 hover:text-red-800 hover:bg-red-50"
        onClick={() => onRemove(stop)}
        data-testid={`route-stop-remove-${stopId(stop)}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Printable route sheet — opens an HTML popup with stops + QR for Google Maps
// Navigation. Same popup-blocker-safe pattern used by the driver checklist
// (synchronous window.open + document.write inside the click handler).
// ────────────────────────────────────────────────────────────────────────────
async function printRouteSheet(routeId, warehouses, mode = "a4") {
  let detail;
  try {
    detail = (await axios.get(`${API}/delivery-routes/${routeId}`, { headers: getAuthHeader() })).data;
  } catch (e) {
    toast.error("Failed to load run for printing");
    return;
  }
  const startWh = detail.start_warehouse || warehouses.find((w) => w.id === detail.start_warehouse_id);
  const endWh = detail.end_warehouse || warehouses.find((w) => w.id === detail.end_warehouse_id) || startWh;

  // Build Google Maps Nav URL. Per Google Maps Universal URL docs (2025):
  // - dir_action=navigate makes the app START navigation instead of just
  //   showing a route preview.
  // - origin should be OMITTED — the driver's device uses its current GPS,
  //   otherwise if the saved warehouse is far from where the driver actually
  //   is, Maps falls back to preview mode.
  // - destination is REQUIRED — we use the end warehouse (round trip).
  // - waypoints separated by literal '|' (the WHOLE value gets URL-encoded).
  // - waypoints are capped at 9 by Google's free URL spec.
  // - duplicate consecutive coords (e.g. two orders at the same hotel) are
  //   collapsed so we don't waste a waypoint slot.
  const stopsWithCoords = (detail.stops || []).filter((s) => s.lat != null && s.lng != null && !s.missing);
  const dedupedWps = [];
  for (const s of stopsWithCoords) {
    const key = `${s.lat},${s.lng}`;
    if (!dedupedWps.includes(key)) dedupedWps.push(key);
  }
  const destLatLng = (endWh?.lat != null && endWh?.lng != null)
    ? `${endWh.lat},${endWh.lng}`
    : (dedupedWps.length ? dedupedWps[dedupedWps.length - 1] : null);
  const navParts = ["api=1"];
  if (destLatLng) navParts.push(`destination=${encodeURIComponent(destLatLng)}`);
  if (dedupedWps.length) {
    // If endWh is the last waypoint, drop it from waypoints (it's already the destination).
    const wpList = (destLatLng === dedupedWps[dedupedWps.length - 1])
      ? dedupedWps.slice(0, -1)
      : dedupedWps;
    if (wpList.length) {
      const wps = wpList.slice(0, 9).join("|");
      navParts.push(`waypoints=${encodeURIComponent(wps)}`);
    }
  }
  navParts.push("travelmode=driving");
  navParts.push("dir_action=navigate");
  const navUrl = `https://www.google.com/maps/dir/?${navParts.join("&")}`;

  // Per-stop QR codes — open just that destination in Google Maps and start
  // navigation immediately (omit origin so the device uses current GPS).
  const perStopQrs = await Promise.all(
    (detail.stops || []).map(async (s) => {
      if (s.lat == null || s.lng == null || s.missing) return null;
      const dest = encodeURIComponent(`${s.lat},${s.lng}`);
      const url = `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving&dir_action=navigate`;
      return QRCode.toDataURL(url, { width: 220, margin: 1 });
    })
  );

  const stops = detail.stops || [];
  const etas = computeStopEtas(
    stops,
    detail.planned_departure_time,
    detail.leg_durations,
    detail.dwell_minutes_per_stop || 30,
  );

  const fmtDist = (m) => (m == null ? "—" : m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`);
  const fmtDur = (s) => {
    if (s == null) return "—";
    const h = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
  };

  const stopsHtml = stops.map((s, i) => {
    const isCustom = s.kind === "custom";
    const title = isCustom ? (s.label || "Custom stop") : (s.hotel_name || "—");
    const sub = isCustom
      ? (s.address || "")
      : `${s.order_number ? `<span style="font-family:monospace">${s.order_number}</span>` : ""}${s.address ? ` · ${s.address}` : ""}`;
    const ohText = formatOpeningHoursForDay(s.opening_hours, detail.delivery_date);
    const oh = ohText
      ? `<div style="font-size:11px;color:#444;margin-top:4px"><strong style="color:#0F4C75">Hours:</strong> ${ohText}</div>`
      : "";
    // Itemized breakdown for order stops — driver knows exactly what
    // was loaded for this hotel. Custom stops have no items.
    let itemsBlock = "";
    if (!isCustom && Array.isArray(s.items) && s.items.length) {
      const lines = s.items
        .map((it) => `<li>${it.quantity} × ${(it.product_name || "—").replace(/</g, "&lt;")}</li>`)
        .join("");
      itemsBlock = `
        <div style="margin-top:6px;background:#f8fafc;border:1px solid #e5e7eb;border-radius:4px;padding:6px 10px">
          <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#0F4C75;font-weight:bold;margin-bottom:2px">
            Items (${s.items_count} pcs total)
          </div>
          <ul style="margin:0;padding-left:18px;font-size:11px;color:#333;line-height:1.4">${lines}</ul>
        </div>`;
    } else if (!isCustom && s.items_count != null) {
      itemsBlock = `<div style="font-size:11px;color:#555;margin-top:4px">${s.items_count} pcs total</div>`;
    }
    const eta = etas[i];
    const etaBlock = eta
      ? `<div style="font-size:11px;color:#0F4C75;margin-top:4px"><strong>Arrive</strong> ${eta.arrive} · <strong>Depart</strong> ${eta.depart}</div>`
      : "";
    const stopQr = perStopQrs[i]
      ? `<img src="${perStopQrs[i]}" style="width:140px;height:140px" alt="Open this stop"/>
         <div style="font-size:10px;color:#000;margin-top:3px;font-weight:600">Scan to navigate</div>`
      : `<div style="width:140px;height:140px;border:1px dashed #000;display:flex;align-items:center;justify-content:center;font-size:10px;color:#000">No coords</div>`;
    return `
      <tr style="border-bottom:1px solid #ddd">
        <td style="padding:8px;vertical-align:top;width:30px;font-family:monospace;font-weight:bold;color:#0F4C75;font-size:18px">${i + 1}</td>
        <td style="padding:8px;vertical-align:top">
          <div style="font-weight:bold;font-size:14px">${title}${isCustom ? ' <span style="background:#ede9fe;color:#5b21b6;padding:1px 6px;border-radius:3px;font-size:10px;margin-left:6px">CUSTOM</span>' : ""}</div>
          <div style="font-size:12px;color:#555;margin-top:2px">${sub}</div>
          ${etaBlock}
          ${oh}
          ${itemsBlock}
          ${!isCustom && s.special_note ? `
            <div style="margin-top:6px;padding:6px 10px;background:#fef3c7;border-left:4px solid #d97706;border-radius:3px">
              <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#92400e;font-weight:bold;margin-bottom:2px">Special note</div>
              <div style="font-size:12px;color:#1f2937;white-space:pre-wrap">${s.special_note.replace(/</g, "&lt;")}</div>
            </div>` : ""}
          ${s.notes ? `<div style="font-size:11px;color:#555;margin-top:4px;font-style:italic">${s.notes}</div>` : ""}
          <div style="margin-top:10px;padding-top:6px;border-top:1px dashed #ccc">
            <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#0F4C75;margin-bottom:8px">Customer signature</div>
            <div style="border-bottom:1px solid #555;height:32px;width:80%"></div>
            <div style="display:flex;gap:24px;font-size:10px;color:#777;margin-top:2px">
              <span>Name + signature</span>
              <span>Time: ____:____</span>
            </div>
          </div>
        </td>
        <td style="padding:8px;vertical-align:top;width:160px;text-align:center" class="stop-qr-cell">
          ${stopQr}
        </td>
        <td style="padding:8px;vertical-align:top;width:50px;text-align:center" class="stop-tick-cell">
          <div style="border:1px solid #999;width:30px;height:30px;margin:0 auto"></div>
          <div style="font-size:9px;color:#777;margin-top:2px">Done</div>
        </td>
      </tr>`;
  }).join("");

  const isLabel = mode === "label";
  const labelCss = `
  body { font-family: -apple-system, system-ui, sans-serif; margin: 0; padding: 3mm; color: #000; width: 76mm; font-size: 12px; }
  .toolbar { display: none; }
  h1 { font-size: 18px; margin: 0 0 2px; color: #000; }
  .meta { display: block; margin-bottom: 6px; }
  .meta-info p { margin: 2px 0; font-size: 11px; font-weight: 600; }
  .meta-info strong { min-width: 62px; display: inline-block; }
  .totals { display: flex; gap: 4px; margin-top: 4px; }
  .totals .box { border: 1px solid #000; padding: 2px 4px; flex: 1; text-align: center; }
  .totals .box .lbl { font-size: 8px; text-transform: uppercase; }
  .totals .box .val { font-size: 12px; font-weight: 800; }
  table { width: 100%; border-collapse: collapse; margin-top: 4px; font-size: 11px; }
  thead { background: #000; color: #fff; }
  thead th { padding: 3px; text-align: left; font-size: 9px; text-transform: uppercase; }
  tbody td { padding: 4px 3px; border-bottom: 1px solid #000; vertical-align: top; }
  /* Force every text element to full black — thermal printers don't
     reproduce grays cleanly and inline "color:#555/#777/#999" would
     otherwise print blank or blurred. Also forces borders black. */
  body, body * { color: #000 !important; }
  body *[style*="color"] { color: #000 !important; }
  body *[style*="border"] { border-color: #000 !important; }
  /* Bump per-stop title & subtitle sizes for readability on 80mm */
  tbody td div { font-size: 12px !important; }
  tbody td div[style*="font-size:14px"] { font-size: 14px !important; font-weight: 800 !important; }
  /* Per-stop QR — big enough to actually be scannable off a receipt roll */
  .stop-qr-cell { width: 30mm !important; padding: 4px 2px !important; }
  .stop-qr-cell img { width: 28mm !important; height: 28mm !important; }
  .stop-qr-cell div { font-size: 8px !important; margin-top: 1px !important; }
  /* Hide the tick box in label mode (drivers use the app to mark done) */
  .stop-tick-cell { display: none; }
  /* Signature footer removed on label mode */
  .footer { display: none; }
  /* Never slice a stop in half across a page/roll break — page breaks
     may only happen BETWEEN stops. */
  tbody tr { page-break-inside: avoid; break-inside: avoid; }
  @page { size: 80mm auto; margin: 0; }
  `;

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Run — ${detail.name || ""} — ${detail.delivery_date || ""}</title>
<style>${isLabel ? labelCss : `
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; margin: 0; padding: 24px; color: #111; }
  .toolbar { position: sticky; top: 0; background: #fff; padding: 8px 0 16px; border-bottom: 1px solid #ddd; margin-bottom: 24px; display: flex; gap: 8px; }
  .toolbar button { padding: 8px 16px; font-size: 14px; cursor: pointer; border: 1px solid #0F4C75; background: #0F4C75; color: #fff; border-radius: 3px; }
  .toolbar button.secondary { background: #fff; color: #0F4C75; }
  h1 { font-size: 22px; margin: 0 0 6px; color: #0F4C75; }
  .meta { display: block; margin-bottom: 16px; }
  .meta-info p { margin: 4px 0; font-size: 13px; }
  .meta-info strong { display: inline-block; min-width: 90px; color: #555; }
  table { width: 100%; border-collapse: collapse; border: 1px solid #ccc; margin-top: 12px; }
  thead { background: #0F4C75; color: #fff; }
  thead th { padding: 8px; text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; }
  .footer { margin-top: 32px; display: grid; grid-template-columns: 1fr 1fr; gap: 24px; font-size: 12px; }
  .footer .sig { border-bottom: 1px solid #000; padding-top: 32px; margin-bottom: 4px; }
  .totals { display: flex; gap: 16px; margin-top: 8px; }
  .totals .box { border: 1px solid #ccc; padding: 8px 12px; border-radius: 4px; }
  .totals .box .lbl { font-size: 10px; text-transform: uppercase; color: #555; letter-spacing: 1px; }
  .totals .box .val { font-size: 16px; font-weight: bold; color: #0F4C75; }
  tbody tr { page-break-inside: avoid; break-inside: avoid; }
  @media print { .toolbar { display: none; } body { padding: 12px; } }
`}
</style></head>
<body>
<div class="toolbar">
  <button onclick="window.print()">🖨 Print</button>
  <button class="secondary" onclick="window.close()">Close</button>
  <button class="secondary" onclick="window.open('${navUrl}','_blank')">🧭 Open in Google Maps</button>
</div>

<h1>${detail.name || `Run ${detail.id.slice(0, 6)}`}</h1>

<div class="meta">
  <div class="meta-info">
    <p><strong>Date:</strong> ${detail.delivery_date || "—"}</p>
    <p><strong>Departure:</strong> ${detail.planned_departure_time || "—"} <span style="font-size:10px;color:#777">(${detail.dwell_minutes_per_stop || 30} min/stop)</span></p>
    <p><strong>Driver:</strong> ${detail.driver || "—"}</p>
    <p><strong>Vehicle:</strong> ${detail.vehicle || "—"}</p>
    <p><strong>Start:</strong> ${startWh?.name || "—"}${startWh?.address ? ` — ${startWh.address}` : ""}</p>
    <p><strong>End:</strong> ${endWh?.name || "—"}${endWh?.address ? ` — ${endWh.address}` : ""}</p>
    ${detail.notes ? `
      <div style="margin-top:8px;padding:8px 12px;background:#fef3c7;border-left:4px solid #d97706;border-radius:3px">
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#92400e;font-weight:bold;margin-bottom:2px">Run Notes</div>
        <div style="font-size:13px;color:#1f2937;white-space:pre-wrap">${detail.notes.replace(/</g, "&lt;")}</div>
      </div>
    ` : ""}
    <div class="totals">
      <div class="box"><div class="lbl">Stops</div><div class="val">${(detail.stops || []).length}</div></div>
      <div class="box"><div class="lbl">Distance</div><div class="val">${fmtDist(detail.total_distance_m)}</div></div>
      <div class="box"><div class="lbl">Duration (incl. ${detail.dwell_minutes_per_stop || 30} min/stop)</div><div class="val">${fmtDur((detail.total_duration_s || 0) + (detail.stops || []).filter((s) => !s.missing).length * ((detail.dwell_minutes_per_stop || 30) * 60))}</div></div>
    </div>
  </div>
</div>

<table>
  <thead><tr><th>#</th><th>Stop · Hours · ETA</th><th style="text-align:center">QR</th><th style="text-align:center">✓</th></tr></thead>
  <tbody>${stopsHtml || '<tr><td colspan="4" style="padding:24px;text-align:center;color:#777">No stops</td></tr>'}</tbody>
</table>

<div class="footer">
  <div><div class="sig"></div>Driver — overall sign-off &amp; final notes</div>
  <div><div class="sig"></div>Time completed (back at depot)</div>
</div>
</body></html>`;

  const w = window.open("", "_blank", isLabel ? "width=340,height=800" : "");
  if (!w) {
    toast.error("Popup blocked. Allow popups for this site to print.");
    return;
  }
  w.document.open();
  w.document.write(html);
  // Label mode auto-prints & closes (matches other thermal-printer flows).
  if (isLabel) {
    w.document.write(`<script>window.onload=()=>{window.print();setTimeout(()=>window.close(),400)}<\/script>`);
  }
  w.document.close();
}

const DAYS = [
  { key: "monday",    short: "Mon" },
  { key: "tuesday",   short: "Tue" },
  { key: "wednesday", short: "Wed" },
  { key: "thursday",  short: "Thu" },
  { key: "friday",    short: "Fri" },
  { key: "saturday",  short: "Sat" },
  { key: "sunday",    short: "Sun" },
];
function formatOpeningHoursInline(oh) {
  if (!oh || typeof oh !== "object") return "";
  const parts = [];
  for (const d of DAYS) {
    const v = oh[d.key];
    if (!v) continue;
    if (v.closed) parts.push(`${d.short} closed`);
    else if (v.open && v.close) parts.push(`${d.short} ${v.open}–${v.close}`);
  }
  return parts.join(" · ");
}

// Single-day variant — only shows opening hours for `deliveryDate` (YYYY-MM-DD).
// Used on the printable route sheet so the driver sees just today's hours.
function formatOpeningHoursForDay(oh, deliveryDate) {
  if (!oh || typeof oh !== "object" || !deliveryDate) return "";
  // Parse the delivery_date as local date and find its weekday key.
  const [y, m, d] = deliveryDate.split("-").map(Number);
  if (!y || !m || !d) return "";
  const dt = new Date(y, m - 1, d);
  // JS getDay(): 0=Sun … 6=Sat. Our DAYS table is Mon-first.
  const jsToDays = [6, 0, 1, 2, 3, 4, 5];
  const day = DAYS[jsToDays[dt.getDay()]];
  if (!day) return "";
  const v = oh[day.key];
  if (!v) return "";
  if (v.closed) return `${day.short} closed`;
  if (v.open && v.close) return `${day.short} ${v.open}–${v.close}`;
  return "";
}

// Compute ETA strings for each stop given a planned departure time + leg
// durations + dwell time. Returns an array of {arrive, depart} HH:MM strings
// the same length as the stops list. When leg durations are unavailable
// (route not yet optimized) returns nulls — caller renders a "—".
function computeStopEtas(stops, plannedDeparture, legDurationsSec, dwellMin) {
  if (!plannedDeparture || !legDurationsSec || legDurationsSec.length === 0) {
    return stops.map(() => null);
  }
  const [hh, mm] = plannedDeparture.split(":").map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return stops.map(() => null);
  const base = new Date();
  base.setHours(hh, mm, 0, 0);
  const dwellSec = (dwellMin || 30) * 60;
  let cursor = base.getTime();
  const out = [];
  for (let i = 0; i < stops.length; i += 1) {
    const drive = (legDurationsSec[i] || 0) * 1000;
    const arrive = new Date(cursor + drive);
    const depart = new Date(arrive.getTime() + dwellSec * 1000);
    const fmt = (d) => d.toTimeString().slice(0, 5);
    out.push({ arrive: fmt(arrive), depart: fmt(depart) });
    cursor = depart.getTime();
  }
  return out;
}

// ────────────────────────────────────────────────────────────────────────────
// Add-a-custom-stop inline form (free-form one-off addresses — fuel depot,
// extra pickup, ad-hoc property). Uses Google Places autocomplete to
// capture coords so the stop can participate in route optimization.
// ────────────────────────────────────────────────────────────────────────────
function CustomStopAdder({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [address, setAddress] = useState("");
  const [coords, setCoords] = useState({ lat: null, lng: null, place_id: null });
  const [notes, setNotes] = useState("");

  const reset = () => {
    setLabel(""); setAddress(""); setCoords({ lat: null, lng: null, place_id: null }); setNotes("");
  };

  const submit = () => {
    if (!label.trim()) { toast.error("Stop label is required"); return; }
    if (coords.lat == null || coords.lng == null) {
      toast.error("Pick an address suggestion so we can geocode this stop");
      return;
    }
    onAdd({
      id: (crypto?.randomUUID?.() || `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
      label: label.trim(),
      address: address.trim() || null,
      lat: coords.lat,
      lng: coords.lng,
      place_id: coords.place_id || null,
      notes: notes.trim() || null,
    });
    reset();
    setOpen(false);
  };

  if (!open) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="border-dashed border-violet-300 text-violet-700 hover:bg-violet-50"
        data-testid="add-custom-stop-toggle"
      >
        <Plus className="w-3.5 h-3.5 mr-1" /> Add custom stop (one-off address)
      </Button>
    );
  }

  return (
    <Card className="border-violet-300 bg-violet-50/40" data-testid="add-custom-stop-form">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-violet-800">New custom stop</span>
          <Button size="icon" variant="ghost" onClick={() => { reset(); setOpen(false); }}
                  data-testid="add-custom-stop-cancel">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Label *</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)}
                   placeholder="e.g. Fuel — BP North"
                   data-testid="custom-stop-label" />
          </div>
          <div>
            <Label className="text-xs">Address *</Label>
            <AddressAutocomplete
              value={address}
              onChange={setAddress}
              onPlaceSelected={(p) => {
                setAddress(p.address);
                setCoords({ lat: p.lat, lng: p.lng, place_id: p.place_id });
              }}
              placeholder="Start typing — pick a Google suggestion"
              testId="custom-stop-address"
            />
            {coords.lat != null && (
              <p className="text-[10px] text-green-700 mt-0.5">
                ✓ Geocoded ({coords.lat.toFixed(4)}, {coords.lng.toFixed(4)})
              </p>
            )}
          </div>
        </div>
        <div>
          <Label className="text-xs">Notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                 placeholder="optional"
                 data-testid="custom-stop-notes" />
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={submit}
                  className="bg-brand-navy text-white hover:bg-brand-navy-hover"
                  data-testid="custom-stop-submit">
            Add to run
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Route editor dialog

// ────────────────────────────────────────────────────────────────────────────
// LiveViewModal — real-time stop-by-stop status for a Run. Polls every 8 s
// while open; shows per-stop done ✓ / next / pending state, plus live ETAs
// recomputed from the anchor (last delivered stop's dispatched_at OR the
// planned_departure_time when nothing done yet).
// ────────────────────────────────────────────────────────────────────────────
function LiveViewModal({ run, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!run) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/delivery-routes/${run.id}/live`, { headers: getAuthHeader() });
        if (!cancelled) setDetail(res.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Failed to load live progress");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [run]);

  if (!run) return null;

  const stops = detail?.stops || [];
  const done = detail?.progress_done || 0;
  const total = detail?.progress_total || 0;
  const nextIdx = stops.findIndex((s) => !s.done);

  return (
    <Dialog open={run !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="run-live-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RouteIcon className="w-5 h-5" />
            {detail?.name || run.name}
            <Badge variant="outline" className="text-xs ml-auto">{detail?.delivery_date || run.delivery_date || "—"}</Badge>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-4 text-xs pt-1">
            {detail?.driver ? <span><User className="inline w-3 h-3 mr-1" />{detail.driver}</span> : null}
            {detail?.vehicle ? <span><Car className="inline w-3 h-3 mr-1" />{detail.vehicle}</span> : null}
            {detail?.planned_departure_time ? <span><Clock className="inline w-3 h-3 mr-1" />Depart {detail.planned_departure_time}</span> : null}
            <span className="ml-auto font-mono text-brand-navy font-bold">{done}/{total} done</span>
          </DialogDescription>
        </DialogHeader>

        {loading && !detail ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-2">
            <div className="h-2 rounded-full bg-neutral-200 overflow-hidden">
              <div
                className={`h-full ${done === total && total > 0 ? "bg-emerald-500" : "bg-brand-navy"} transition-all`}
                style={{ width: total > 0 ? `${(done / total) * 100}%` : "0%" }}
              />
            </div>

            {stops.length === 0 && (
              <p className="text-sm text-muted-foreground italic py-4 text-center">No stops in this run.</p>
            )}
            {stops.map((s, i) => {
              const isNext = i === nextIdx;
              const title = s.kind === "custom" ? (s.label || "Custom stop") : (s.hotel_name || "—");
              return (
                <div
                  key={i}
                  data-testid={`live-stop-${i}`}
                  className={`rounded-md border px-3 py-2 flex items-center gap-3 ${
                    s.done
                      ? "bg-emerald-50/60 border-emerald-200"
                      : isNext
                      ? "bg-amber-50 border-amber-300 border-2"
                      : "bg-white border-neutral-200"
                  }`}
                >
                  <span className={`font-mono font-bold w-7 text-center shrink-0 ${
                    s.done ? "text-emerald-600" : isNext ? "text-amber-700" : "text-neutral-500"
                  }`}>{i + 1}</span>
                  <div className="shrink-0">
                    {s.done
                      ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      : isNext
                      ? <AlertCircle className="w-5 h-5 text-amber-600" />
                      : <span className="w-5 h-5 inline-block rounded-full border-2 border-neutral-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`font-semibold text-sm truncate ${s.done ? "text-emerald-800" : ""}`}>{title}</span>
                      {s.kind === "custom" && (
                        <Badge className="bg-violet-100 text-violet-800 text-[9px]">custom</Badge>
                      )}
                      {isNext && !s.done && (
                        <Badge className="bg-amber-500 text-white text-[9px] uppercase tracking-wider">next</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {s.order_number && <span className="font-mono mr-2">{s.order_number}</span>}
                      {s.address}
                      {s.items_count != null && ` · ${s.items_count} pcs`}
                    </div>
                    {s.special_note && (
                      <div className="mt-1 text-[11px] text-amber-900 bg-amber-100 border-l-2 border-amber-500 pl-2 py-0.5 rounded-r whitespace-pre-wrap break-words" data-testid={`live-stop-special-note-${i}`}>
                        <span className="font-bold">Note:</span> {s.special_note}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-xs shrink-0">
                    {s.done ? (
                      <span className="text-emerald-700 font-semibold">Delivered</span>
                    ) : s.live_eta ? (
                      <div>
                        <div className="text-neutral-500 text-[10px] uppercase tracking-wider">ETA</div>
                        <div className="font-mono font-bold text-brand-navy">{s.live_eta.arrive}</div>
                      </div>
                    ) : (
                      <span className="text-neutral-400 text-[10px]">—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// PickingSummaryModal — total qty per product across the whole run.
// Available the moment a run is created (reads Order.items — no bags
// needed). The warehouse team prints this to pre-pick stock BEFORE the
// packing operator starts assembling bags. Two print variants: 80mm
// thermal label and A4 for filing.
// ────────────────────────────────────────────────────────────────────────────
function PickingSummaryModal({ run, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!run) return undefined;
    let cancelled = false;
    setLoading(true); setData(null);
    (async () => {
      try {
        const res = await axios.get(
          `${API}/delivery-routes/${run.id}/picking-summary`,
          { headers: getAuthHeader() },
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Failed to load picking summary");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [run]);

  const doPrint = () => {
    if (!data) return;
    const rowsHtml = (data.items || []).map((it) => `
      <tr><td>${(it.product_name || "—")}</td><td class="q">${it.quantity}</td></tr>
    `).join("");
    // 80mm thermal receipt — pure black only (greys print faint/blank on
    // receipt printers).
    const css = `
      body { font-family: -apple-system, system-ui, sans-serif; width: 72mm; margin: 0 auto; padding: 2mm; color: #000; }
      body, body * { color: #000 !important; }
      .toolbar { display: flex; gap: 8px; padding: 8px 0; border-bottom: 1px solid #000; margin-bottom: 8px; }
      .toolbar button { padding: 8px 16px; font-size: 14px; cursor: pointer; border: 1px solid #000; background: #000; color: #fff !important; border-radius: 3px; }
      .toolbar button.secondary { background: #fff; color: #000 !important; }
      h1 { margin: 0 0 4px; font-size: 18px; }
      .meta { font-size: 12px; font-weight: 700; margin-bottom: 4px; }
      table { width: 100%; border-collapse: collapse; font-size: 14px; margin-top: 6px; }
      td { padding: 5px 2px; border-bottom: 1px dashed #000; vertical-align: top; }
      .q { text-align: right; font-weight: 800; font-family: monospace; white-space: nowrap; font-size: 16px; }
      tfoot td { border-top: 2px solid #000; border-bottom: none; font-weight: 800; font-size: 17px; }
      @page { size: 80mm auto; margin: 0; }
      @media print { .toolbar { display: none; } body { margin: 0; } }
    `;
    const html = `<!doctype html><html><head><meta charset="utf-8">
      <title>Picking summary — ${run.name || ""}</title><style>${css}</style></head><body>
      <h1>Picking summary</h1>
      <div class="meta">${run.name || "Run"} · ${data.route?.delivery_date || "—"}${data.route?.driver ? " · " + data.route.driver : ""}</div>
      <div class="meta">${data.order_count} orders · ${data.total_pcs} pcs · ${(data.items || []).length} products</div>
      <table>
        <tbody>${rowsHtml || '<tr><td colspan="2" style="text-align:center;color:#888;padding:10px 0">— empty —</td></tr>'}</tbody>
        <tfoot><tr><td>TOTAL</td><td class="q">${data.total_pcs}</td></tr></tfoot>
      </table>
      </body></html>`;
    printHtmlViaIframe(html);
  };

  if (!run) return null;
  return (
    <Dialog open={run !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="picking-summary-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageOpen className="w-5 h-5" />Picking summary · {run.name || "Run"}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-3 text-xs pt-1 flex-wrap">
            <Badge variant="outline">{run.delivery_date || "—"}</Badge>
            {run.driver && <span><User className="inline w-3 h-3 mr-1" />{run.driver}</span>}
            {data && (
              <span className="ml-auto font-mono text-brand-navy font-bold">
                {data.order_count} orders · {data.total_pcs} pcs
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading && !data ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : !data?.items?.length ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No orders on this run yet. Add orders in <strong>Edit</strong> first.
          </div>
        ) : (
          <div className="rounded-md border bg-white overflow-hidden">
            <div className="grid grid-cols-[1fr_auto] gap-4 px-3 py-2 bg-neutral-50 border-b text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
              <span>Product</span><span>Total pcs</span>
            </div>
            <div className="divide-y">
              {data.items.map((it) => (
                <div key={it.product_id || it.product_name} className="grid grid-cols-[1fr_auto] gap-4 px-3 py-1.5 text-sm">
                  <span className="truncate">{it.product_name}</span>
                  <span className="font-mono font-bold text-brand-navy tabular-nums">{it.quantity}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-4 px-3 py-2 bg-neutral-50 border-t text-sm font-bold">
              <span>Total</span>
              <span className="font-mono text-brand-navy tabular-nums">{data.total_pcs}</span>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={onClose} data-testid="picking-summary-close">
            <X className="w-4 h-4 mr-1" />Close
          </Button>
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={() => doPrint()}
            disabled={!data?.items?.length}
            data-testid="picking-summary-print-a4"
            className="bg-brand-navy hover:bg-brand-navy-hover text-white border-brand-navy"
          >
            <Printer className="w-4 h-4 mr-1" />Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function RouteEditor({ open, onClose, route, warehouses, onSaved }) {
  const [name, setName] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(todayIsoLocal());
  const [startWh, setStartWh] = useState("");
  const [endWh, setEndWh] = useState("");
  const [driver, setDriver] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [notes, setNotes] = useState("");
  const [plannedDeparture, setPlannedDeparture] = useState("");
  const [dwellMinutes, setDwellMinutes] = useState(30);
  const [autoOptimize, setAutoOptimize] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stops, setStops] = useState([]); // enriched stop objects in order
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  // Filters + sort for the "Available orders" picker. Only two controls:
  // filter by service-route (empty == all) and toggle the chronological
  // sort direction of the date groups.
  const [filterRouteId, setFilterRouteId] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  // Personnel directories — power the <datalist> autocomplete on the
  // Driver/Vehicle inputs. Free-text is still accepted; this just makes
  // typing easier.
  const [driverOptions, setDriverOptions] = useState([]);
  const [vehicleOptions, setVehicleOptions] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Load APPROVED orders (packed or not — packing happens AFTER run
  // assignment on the packing screen) not already claimed by another
  // pending/planned run.
  const loadAvailable = async () => {
    setLoadingAvailable(true);
    try {
      const [queueRes, routesRes] = await Promise.all([
        axios.get(`${API}/dispatch/queue?status=assignable`, { headers: getAuthHeader() }),
        axios.get(`${API}/delivery-routes`, { headers: getAuthHeader() }),
      ]);
      const claimedByOther = new Set();
      for (const r of routesRes.data || []) {
        if (r.status === "completed") continue;
        if (route?.id && r.id === route.id) continue;
        for (const oid of (r.order_ids || [])) claimedByOther.add(oid);
      }
      const rows = Array.isArray(queueRes.data) ? queueRes.data : (queueRes.data?.items || []);
      const orderRows = rows.filter(
        (r) => r.kind === "order" && !r.is_pickup && !claimedByOther.has(r.order_id)
      );
      const normalised = orderRows.map((r) => ({
        id: r.order_id,
        order_number: r.order_number,
        hotel_name: r.hotel_name,
        delivery_date: r.delivery_date,
        is_pickup: r.is_pickup,
        items: r.items || [],
        packed_pcs: r.packed_pcs ?? 0,
        lat: r.lat,
        lng: r.lng,
        address: r.hotel_address,
        place_id: r.place_id,
        route_id: r.route_id || "",
        route_name: r.route_name || "",
      }));
      setAvailableOrders(normalised);
      return normalised;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load orders");
      return [];
    } finally {
      setLoadingAvailable(false);
    }
  };

  // Load route detail (or fresh defaults) + load available orders. For NEW
  // runs, also auto-set deliveryDate to the closest upcoming delivery_date
  // observed in the available orders (the operator can still change it).
  useEffect(() => {
    if (!open) return;
    // Fire-and-forget personnel directory lookup. Errors are non-blocking
    // (free-text input still works without suggestions).
    (async () => {
      try {
        const [dRes, vRes] = await Promise.all([
          axios.get(`${API}/drivers`, { headers: getAuthHeader() }),
          axios.get(`${API}/vehicles`, { headers: getAuthHeader() }),
        ]);
        setDriverOptions(dRes.data || []);
        setVehicleOptions(vRes.data || []);
      } catch (_e) {
        // ignore — autocomplete is a nice-to-have
      }
    })();
    (async () => {
      if (route?.id) {
        try {
          const r = (await axios.get(`${API}/delivery-routes/${route.id}`, { headers: getAuthHeader() })).data;
          setName(r.name || "");
          setDeliveryDate(r.delivery_date || todayIsoLocal());
          setStartWh(r.start_warehouse_id || "");
          setEndWh(r.end_warehouse_id || "");
          setDriver(r.driver || "");
          setVehicle(r.vehicle || "");
          setNotes(r.notes || "");
          setPlannedDeparture(r.planned_departure_time || "");
          setDwellMinutes(r.dwell_minutes_per_stop || 30);
          // Enriched stops returned by /delivery-routes/{id} include both
          // 'order' and 'custom' kinds in stop_sequence order.
          setStops(r.stops || []);
        } catch (e) {
          toast.error("Failed to load run");
        }
        await loadAvailable();
      } else {
        setName("");
        const primary = warehouses.find((w) => w.is_primary && !w.is_archived);
        setStartWh(primary?.id || "");
        setEndWh(primary?.id || "");
        setDriver("");
        setVehicle("");
        setNotes("");
        setPlannedDeparture("");
        setDwellMinutes(30);
        setAutoOptimize(true);
        setStops([]);
        const orders = await loadAvailable();
        // Pick the closest upcoming delivery date present in available
        // orders. Fall back to today.
        const today = todayIsoLocal();
        const allDates = Array.from(
          new Set(orders.map((o) => o.delivery_date).filter(Boolean))
        ).sort();
        const upcoming = allDates.find((d) => d >= today);
        const closest = upcoming || allDates[allDates.length - 1] || today;
        setDeliveryDate(closest);
      }
    })();
  }, [open, route?.id]);

  const toggleStopFromOrder = (order) => {
    setStops((prev) => {
      const exists = prev.find((s) => s.kind !== "custom" && (s.order_id || s.id) === order.id);
      if (exists) return prev.filter((s) => !(s.kind !== "custom" && (s.order_id || s.id) === order.id));
      const newStop = {
        kind: "order",
        order_id: order.id,
        id: order.id,
        order_number: order.order_number,
        hotel_name: order.hotel_name,
        delivery_date: order.delivery_date,
        is_pickup: order.is_pickup,
        items_count: (order.items || []).reduce((s, i) => s + (i.quantity || 0), 0),
        lat: order.lat ?? null,
        lng: order.lng ?? null,
        address: order.address ?? null,
        place_id: order.place_id ?? null,
      };
      return [...prev, newStop];
    });
  };

  const addCustomStop = (cs) => {
    setStops((prev) => [...prev, { ...cs, kind: "custom" }]);
  };

  const onDragEnd = (ev) => {
    const { active, over } = ev;
    if (!over || active.id === over.id) return;
    setStops((items) => {
      const oldIdx = items.findIndex((it) => stopId(it) === active.id);
      const newIdx = items.findIndex((it) => stopId(it) === over.id);
      return arrayMove(items, oldIdx, newIdx);
    });
  };

  const removeStop = (stop) => {
    const sid = stopId(stop);
    setStops((prev) => prev.filter((s) => stopId(s) !== sid));
  };

  const save = async () => {
    const orderIds = stops
      .filter((s) => s.kind !== "custom")
      .map((s) => s.order_id || s.id);
    const customs = stops
      .filter((s) => s.kind === "custom")
      .map((s) => ({
        id: s.id,
        label: s.label,
        address: s.address || null,
        lat: s.lat ?? null,
        lng: s.lng ?? null,
        place_id: s.place_id || null,
        notes: s.notes || null,
      }));
    const sequence = stops.map((s) =>
      s.kind === "custom"
        ? { kind: "custom", id: s.id }
        : { kind: "order", id: s.order_id || s.id }
    );
    const payload = {
      name: name.trim() || [driver.trim(), vehicle.trim(), deliveryDate].filter(Boolean).join(" · ") || `Run ${deliveryDate}`,
      delivery_date: deliveryDate || null,
      start_warehouse_id: startWh || null,
      end_warehouse_id: endWh || null,
      order_ids: orderIds,
      custom_stops: customs,
      stop_sequence: sequence,
      driver: driver.trim() || null,
      vehicle: vehicle.trim() || null,
      notes: notes || null,
      planned_departure_time: plannedDeparture || null,
      dwell_minutes_per_stop: dwellMinutes || 30,
    };
    setSaving(true);
    try {
      let savedId;
      if (route?.id) {
        await axios.patch(`${API}/delivery-routes/${route.id}`, payload, { headers: getAuthHeader() });
        savedId = route.id;
        toast.success("Run saved");
      } else {
        const resp = await axios.post(`${API}/delivery-routes`, payload, { headers: getAuthHeader() });
        savedId = resp.data?.id;
        toast.success("Run created");
      }
      // Auto-optimize after save if requested and at least one stop has
      // coords. Errors here are non-blocking — the run is already saved.
      const hasGeocodedStop = stops.some(
        (s) => s.lat != null && s.lng != null && !s.missing,
      );
      if (autoOptimize && savedId && hasGeocodedStop && startWh) {
        try {
          toast.info("Optimizing route…");
          const opt = await axios.post(
            `${API}/delivery-routes/${savedId}/optimize`, {},
            { headers: getAuthHeader() },
          );
          const km = ((opt.data.total_distance_m || 0) / 1000).toFixed(1);
          const mins = Math.round(((opt.data.total_duration_s || 0) + stops.length * (dwellMinutes || 30) * 60) / 60);
          toast.success(`Optimized — ${km} km · ${mins} min`);
          if (opt.data.skipped_stops) {
            toast.warning(`${opt.data.skipped_stops} stop(s) without coords kept at end`);
          }
        } catch (e) {
          toast.warning(
            `Auto-optimize failed — ${e?.response?.data?.detail?.slice(0, 80) || "you can retry from the card"}`
          );
        }
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save run");
    } finally {
      setSaving(false);
    }
  };

  // Distinct route options, derived from the unfiltered list so toggling
  // the route filter doesn't hide the other options.
  const routeOptions = useMemo(() => {
    const m = new Map();
    for (const o of availableOrders) {
      const id = o.route_id || "__none__";
      const label = o.route_name || (o.route_id ? o.route_id : "No route");
      if (!m.has(id)) m.set(id, label);
    }
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [availableOrders]);

  // Group available orders by date → route for the picker, with the
  // route filter + sort applied. Structure:
  //   [ [date, [ [routeLabel, [orders...]], ... ]], ... ]
  const groupedAvailable = useMemo(() => {
    const filtered = availableOrders.filter((o) => {
      if (!filterRouteId) return true;
      const rid = o.route_id || "__none__";
      return rid === filterRouteId;
    });
    // date -> Map(routeLabel -> orders[])
    const byDate = new Map();
    for (const o of filtered) {
      const d = o.delivery_date || "No date";
      const r = o.route_name || (o.route_id ? o.route_id : "No route");
      if (!byDate.has(d)) byDate.set(d, new Map());
      const routes = byDate.get(d);
      if (!routes.has(r)) routes.set(r, []);
      routes.get(r).push(o);
    }
    return Array.from(byDate.entries())
      .sort(([a], [b]) => (sortDir === "desc" ? b.localeCompare(a) : a.localeCompare(b)))
      .map(([d, routes]) => [
        d,
        Array.from(routes.entries()).sort(([a], [b]) => a.localeCompare(b)),
      ]);
  }, [availableOrders, filterRouteId, sortDir]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="route-editor-dialog">
        <DialogHeader>
          <DialogTitle>{route?.id ? "Edit delivery run" : "New delivery run"}</DialogTitle>
          <DialogDescription>
            Pick start + end warehouses and the orders to include. Drag stops to reorder.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 px-1">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Run name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={[driver.trim(), vehicle.trim(), deliveryDate].filter(Boolean).join(" · ") || "Driver · Vehicle · Date"}
                data-testid="route-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Delivery date</Label>
              <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} data-testid="route-date-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Start warehouse</Label>
              <Select value={startWh || ""} onValueChange={setStartWh}>
                <SelectTrigger data-testid="route-start-warehouse"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.filter((w) => !w.is_archived).map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      <WarehouseIcon className="inline w-3 h-3 mr-1" />{w.name}{w.is_primary ? " (primary)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">End warehouse</Label>
              <Select value={endWh || ""} onValueChange={setEndWh}>
                <SelectTrigger data-testid="route-end-warehouse"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.filter((w) => !w.is_archived).map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      <WarehouseIcon className="inline w-3 h-3 mr-1" />{w.name}{w.is_primary ? " (primary)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* STOPS — drag to reorder */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">
              Stops in this run ({stops.length}) — drag to reorder
            </Label>
            <div className="mt-2">
              {stops.length === 0 ? (
                <Card><CardContent className="p-4 text-center text-sm text-muted-foreground">
                  No stops yet. Tick orders below or add a custom stop.
                </CardContent></Card>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                  <SortableContext items={stops.map((s) => stopId(s))} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1.5">
                      {stops.map((s, i) => (
                        <SortableStopRow key={stopId(s)} stop={s} index={i} onRemove={removeStop} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>

          {/* CUSTOM STOPS — free-form one-off addresses */}
          <CustomStopAdder onAdd={addCustomStop} />

          {/* AVAILABLE ORDERS — packed, not claimed by another active run.
              Tick to add as a stop; untick to remove. */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">
              Available orders {availableOrders.length > 0 ? `(${availableOrders.length})` : ""}
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">
              Approved orders — packed or not (packing happens per-run after assignment). Pickup orders + orders in another active run are hidden.
            </p>
            {/* Master filter bar: filter by service-route + toggle
                chronological sort. Empty selection == show all. */}
            {availableOrders.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Select
                  value={filterRouteId || "__all__"}
                  onValueChange={(v) => setFilterRouteId(v === "__all__" ? "" : v)}
                >
                  <SelectTrigger className="w-48 h-8 text-xs" data-testid="available-filter-route">
                    <SelectValue placeholder="All routes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All routes</SelectItem>
                    {routeOptions.map(([id, label]) => (
                      <SelectItem key={id} value={id}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs"
                  onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                  data-testid="available-sort-toggle"
                  title="Toggle chronological sort"
                >
                  <Calendar className="w-3.5 h-3.5 mr-1" />
                  {sortDir === "asc" ? "Earliest → Latest" : "Latest → Earliest"}
                </Button>
                {filterRouteId && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-8 text-xs text-muted-foreground"
                    onClick={() => setFilterRouteId("")}
                    data-testid="available-filter-clear"
                  >
                    Clear filter
                  </Button>
                )}
              </div>
            )}
            <div className="border rounded-md bg-neutral-50 max-h-80 overflow-y-auto">
              {loadingAvailable && (
                <p className="text-center text-sm text-muted-foreground py-6">Loading…</p>
              )}
              {!loadingAvailable && availableOrders.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-6">
                  No orders available. All packed orders are either already in this run or assigned to another active run.
                </p>
              )}
              {!loadingAvailable && availableOrders.length > 0 && groupedAvailable.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-6">
                  No orders match the current filters.
                </p>
              )}
              {!loadingAvailable && groupedAvailable.map(([date, routeGroups]) => {
                const stopIds = new Set(stops.filter((s) => s.kind !== "custom").map((s) => s.order_id || s.id));
                const dateOrders = routeGroups.flatMap(([, list]) => list);
                const allDateInRun = dateOrders.every((o) => stopIds.has(o.id));
                const toggleDate = () => {
                  if (allDateInRun) {
                    setStops((prev) => prev.filter((s) => s.kind === "custom" || !dateOrders.some((o) => o.id === (s.order_id || s.id))));
                  } else {
                    dateOrders.forEach((o) => { if (!stopIds.has(o.id)) toggleStopFromOrder(o); });
                  }
                };
                return (
                  <div key={date} className="bg-white border-b last:border-b-0">
                    <div className="flex items-center gap-2 px-2 py-1.5 bg-neutral-100 border-b sticky top-0 z-10">
                      <Checkbox checked={allDateInRun} onCheckedChange={toggleDate} />
                      <Calendar className="w-3.5 h-3.5 text-brand-navy" />
                      <span className="font-bold text-sm text-brand-navy">{date}</span>
                      <span className="text-xs text-muted-foreground">· {dateOrders.length}</span>
                    </div>
                    {routeGroups.map(([routeLabel, list]) => {
                      const allRouteInRun = list.every((o) => stopIds.has(o.id));
                      const toggleRoute = () => {
                        if (allRouteInRun) {
                          setStops((prev) => prev.filter((s) => s.kind === "custom" || !list.some((o) => o.id === (s.order_id || s.id))));
                        } else {
                          list.forEach((o) => { if (!stopIds.has(o.id)) toggleStopFromOrder(o); });
                        }
                      };
                      return (
                        <div key={routeLabel} className="border-b last:border-b-0">
                          <div className="flex items-center gap-2 px-2 py-1 pl-6 bg-neutral-50 border-b">
                            <Checkbox checked={allRouteInRun} onCheckedChange={toggleRoute} />
                            <RouteIcon className="w-3 h-3 text-neutral-500" />
                            <span className="text-xs font-semibold text-neutral-700">{routeLabel}</span>
                            <span className="text-[10px] text-muted-foreground">· {list.length}</span>
                          </div>
                          {list.map((o) => {
                            const inRun = stopIds.has(o.id);
                            return (
                              <label
                                key={o.id}
                                className="flex items-center gap-2 px-2 py-1.5 pl-10 hover:bg-neutral-50 cursor-pointer border-b last:border-b-0 text-sm"
                              >
                                <Checkbox
                                  checked={inRun}
                                  onCheckedChange={() => toggleStopFromOrder(o)}
                                  data-testid={`route-available-order-${o.id}`}
                                />
                                <span className="font-mono text-xs">{o.order_number}</span>
                                <span className="truncate flex-1">{o.hotel_name}</span>
                                {(() => {
                                  const totalPcs = (o.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
                                  const fullyPacked = totalPcs > 0 && (o.packed_pcs ?? 0) >= totalPcs;
                                  return (
                                    <>
                                      <span className="text-xs text-muted-foreground">{totalPcs} pcs</span>
                                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                                        fullyPacked
                                          ? "bg-green-50 text-green-700 border-green-200"
                                          : "bg-amber-50 text-amber-700 border-amber-200"
                                      }`}>
                                        {fullyPacked ? "Packed" : "Not packed"}
                                      </span>
                                    </>
                                  );
                                })()}
                              </label>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">
                <User className="inline w-3 h-3 mr-1" />Driver
              </Label>
              <Input
                value={driver}
                onChange={(e) => setDriver(e.target.value)}
                placeholder="Driver name"
                list="driver-options"
                data-testid="route-driver-input"
              />
              <datalist id="driver-options">
                {driverOptions.map((d) => (
                  <option key={d.id} value={d.name}>
                    {d.phone ? `${d.phone}` : ""}
                  </option>
                ))}
              </datalist>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">
                <Car className="inline w-3 h-3 mr-1" />Vehicle
              </Label>
              <Input
                value={vehicle}
                onChange={(e) => setVehicle(e.target.value)}
                placeholder="Vehicle / rego (e.g. ABC123)"
                list="vehicle-options"
                data-testid="route-vehicle-input"
              />
              <datalist id="vehicle-options">
                {vehicleOptions.map((v) => (
                  <option key={v.id} value={v.label}>
                    {v.description || ""}
                  </option>
                ))}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">
                <Clock className="inline w-3 h-3 mr-1" />Planned departure
              </Label>
              <Input
                type="time"
                value={plannedDeparture}
                onChange={(e) => setPlannedDeparture(e.target.value)}
                data-testid="route-planned-departure-input"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Drives ETA on the printed sheet
              </p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Minutes per stop</Label>
              <Input
                type="number"
                min={5}
                max={240}
                value={dwellMinutes}
                onChange={(e) => setDwellMinutes(parseInt(e.target.value, 10) || 30)}
                data-testid="route-dwell-input"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Service time at each hotel
              </p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Auto-optimize</Label>
              <label className="flex items-center gap-2 h-9 cursor-pointer text-sm" data-testid="route-auto-optimize-toggle">
                <input
                  type="checkbox"
                  checked={autoOptimize}
                  onChange={(e) => setAutoOptimize(e.target.checked)}
                />
                <span>Run Google Routes optimizer on save</span>
              </label>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Driver notes, special instructions…" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="route-save-btn">
            {saving ? "Saving…" : (route?.id ? "Save" : "Create run")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main view
// ────────────────────────────────────────────────────────────────────────────
export default function DispatchRoutesView() {
  const [routes, setRoutes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  // window.confirm() is silently dismissed in some WebViews / strict browsers,
  // so we drive the destructive confirm via a real AlertDialog instead.
  const [confirmDelete, setConfirmDelete] = useState(null); // route obj or null
  const [liveViewRun, setLiveViewRun] = useState(null); // for the "Live" modal
  const [packRunId, setPackRunId] = useState(null); // run being packed (touch board)
  const [pickingRun, setPickingRun] = useState(null); // run whose picking-summary modal is open
  const [pickupPackDetail, setPickupPackDetail] = useState(null); // { detail, hotels, products } for a standalone pickup pack modal
  const [pickupProducts, setPickupProducts] = useState([]); // full product catalog for pack modal
  const [pickupHotels, setPickupHotels] = useState([]); // hotel users for pack modal
  const [completedPickups, setCompletedPickups] = useState([]); // dispatched orders (both delivered runs + picked-up pickups, last 20)
  const [recentExpanded, setRecentExpanded] = useState(false); // Recent delivered section collapsed by default
  const [unassigned, setUnassigned] = useState([]); // approved orders not on any active run
  const [packedOrders, setPackedOrders] = useState([]); // orders with packed bags awaiting delivery
  const [confirmDeliver, setConfirmDeliver] = useState(null); // order obj for desktop-deliver confirm
  const [delivering, setDelivering] = useState(false);
  // Multi-select for pickup orders — planner ticks checkboxes to
  // aggregate a single "how many total pcs do I need to grab from the
  // warehouse" packing list across N pickups.
  const [selectedPickupIds, setSelectedPickupIds] = useState(() => new Set());
  const [pickupCombinedOpen, setPickupCombinedOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, w, q, comp, pk] = await Promise.all([
        axios.get(`${API}/delivery-routes?include_progress=true`, { headers: getAuthHeader() }),
        axios.get(`${API}/warehouses`, { headers: getAuthHeader() }),
        axios.get(`${API}/dispatch/queue?status=assignable`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        // Completed pickups history: fetch all orders (admin sees all)
        // and filter to dispatched pickups locally. Cheap enough at
        // small-to-medium scale (bounded to last 20 anyway).
        axios.get(`${API}/orders?group_by=none`, { headers: getAuthHeader() }).catch(() => ({ data: { orders: [] } })),
        axios.get(`${API}/dispatch/queue?status=packed`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
      ]);
      setRoutes(r.data || []);
      setWarehouses(w.data || []);
      setPackedOrders((pk.data || []).filter((o) => !o.is_pickup));
      const claimed = new Set();
      for (const run of (r.data || [])) {
        if (run.status === "completed") continue;
        for (const oid of (run.order_ids || [])) claimed.add(oid);
      }
      // Include pickup orders in the "unassigned" list below (they need
      // a place to live so the office can mark them picked up), but the
      // RouteEditor already filters them out of the "assignable" pool.
      const rows = (q.data || []).filter((o) => !claimed.has(o.order_id));
      rows.sort((a, b) => (a.delivery_date || "").localeCompare(b.delivery_date || ""));
      setUnassigned(rows);
      // Recent-delivered feed: dispatched orders — both physically-
      // delivered runs and marked-picked-up pickups. Newest first.
      const allOrders = Array.isArray(comp.data) ? comp.data : (comp.data?.orders || []);
      const recent = allOrders
        .filter((o) => o.status === "dispatched")
        .sort((a, b) => (b.dispatched_at || b.updated_at || "").localeCompare(a.dispatched_at || a.updated_at || ""))
        .slice(0, 20);
      setCompletedPickups(recent);
    } catch (e) {
      toast.error("Failed to load runs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Poll every 15 s so the "Live" progress bars on each Run card refresh
    // while the driver is out on the road. Cheap query — one row per run.
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const setStatus = async (r, status) => {
    try {
      await axios.post(`${API}/delivery-routes/${r.id}/status`, { status }, { headers: getAuthHeader() });
      toast.success(`Status → ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  // setStatus is retained for future inline status changes inside the editor;
  // the card-level Mark planned/completed buttons were removed per user
  // feedback ("外面无所谓").
  void setStatus;

  const remove = async (r) => {
    if (!r) return;
    try {
      await axios.delete(`${API}/delivery-routes/${r.id}`, { headers: getAuthHeader() });
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setConfirmDelete(null);
    }
  };

  // Pickup orders don't go through packing / delivery — one click flips
  // them straight to `dispatched` and removes them from the unassigned list.
  const markPickedUp = async (o) => {
    try {
      await axios.post(`${API}/dispatch/orders/${o.order_id}/mark-picked-up`, {}, { headers: getAuthHeader() });
      toast.success(`${o.order_number} marked as picked up`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark picked up");
    }
  };

  // Desktop mark-delivered — all packed bags of the order flip to delivered
  // without a PDA scan (office confirms delivery happened).
  const desktopDeliver = async (o) => {
    setDelivering(true);
    try {
      const res = await axios.post(`${API}/dispatch/orders/${o.order_id}/desktop-deliver`, {}, { headers: getAuthHeader() });
      toast.success(`${o.order_number} delivered — ${res.data.bags_delivered} bag(s)${res.data.order_status === "dispatched" ? ", order dispatched" : ""}`);
      setConfirmDeliver(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to mark delivered");
    } finally {
      setDelivering(false);
    }
  };

  // Standalone Pack modal for a single pickup order (they aren't on any
  // delivery run so the normal RunPackingBoard doesn't apply). We fetch
  // the bundle detail + products + hotels on demand and hand them to the
  // reused PackStopModal component.
  const openPickupPack = async (o) => {
    try {
      const [detailRes, prodRes, hotelRes] = await Promise.all([
        axios.get(`${API}/dispatch/orders/${o.order_id}/bundles`, { headers: getAuthHeader() }),
        pickupProducts.length ? Promise.resolve({ data: pickupProducts }) : axios.get(`${API}/products`, { headers: getAuthHeader() }),
        pickupHotels.length ? Promise.resolve({ data: pickupHotels }) : axios.get(`${API}/users`, { headers: getAuthHeader() }),
      ]);
      if (!pickupProducts.length) setPickupProducts(prodRes.data || []);
      if (!pickupHotels.length) setPickupHotels(hotelRes.data || []);
      setPickupPackDetail(detailRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to open pack");
    }
  };

  const filtered = useMemo(() => {
    // "Auto-move" done runs to the Recent delivered feed — a run whose
    // every ORDER is dispatched is considered finished and hidden from
    // the main list unless the operator explicitly filters for
    // `completed`. Custom stops (warehouse-return points, etc.) are
    // intentionally ignored: they don't have a status so they'd
    // otherwise permanently inflate progress_total and keep the run
    // visible forever.
    const isDone = (r) => {
      if (r.status === "completed") return true;
      if (r.all_orders_dispatched) return true;
      // Fallback for old cached data missing the new flag.
      const orderCount = (r.order_ids || []).length;
      const done = r.progress_done ?? 0;
      return orderCount > 0 && done >= orderCount;
    };
    if (statusFilter === "all") return routes.filter((r) => !isDone(r));
    if (statusFilter === "completed") return routes.filter(isDone);
    return routes.filter((r) => r.status === statusFilter && !isDone(r));
  }, [routes, statusFilter]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const k = r.status || "pending";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    return ["pending", "planned", "completed"].map((k) => [k, map.get(k) || []]);
  }, [filtered]);

  if (packRunId) {
    return <RunPackingBoard routeId={packRunId} onBack={() => { setPackRunId(null); load(); }} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <RouteIcon className="h-6 w-6 text-brand-navy" />
          <div>
            <h2 className="font-display text-2xl font-bold text-brand-navy tracking-tight">Delivery runs</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Plan a delivery run by selecting warehouses + orders. Drag to reorder stops.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="routes-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="planned">Planned</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => { setEditingRoute(null); setEditorOpen(true); }} data-testid="route-create-btn">
            <Plus className="w-4 h-4 mr-1" />New run
          </Button>
        </div>
      </div>

      {warehouses.length === 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 text-sm">
            <strong>Add a warehouse first</strong> — go to Settings → Warehouses to add at least one depot before creating runs.
          </CardContent>
        </Card>
      )}

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!loading && filtered.length === 0 && (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
          No runs {statusFilter !== "all" ? `with status "${statusFilter}"` : "yet"}. Click <strong>New run</strong> to plan one.
        </CardContent></Card>
      )}

      {grouped.map(([status, list]) => list.length > 0 && (
        <div key={status} className="space-y-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {STATUS_LABEL[status]?.text || status} · {list.length}
          </h3>
          <div className="space-y-2">
            {list.map((r) => {
              const startName = warehouses.find((w) => w.id === r.start_warehouse_id)?.name;
              const endName = warehouses.find((w) => w.id === r.end_warehouse_id)?.name;
              return (
                <Card key={r.id} className="border hover:border-brand-navy transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-base">{r.name || `Run ${r.id.slice(0, 6)}`}</span>
                          <Badge className={STATUS_LABEL[r.status]?.className || ""}>
                            {STATUS_LABEL[r.status]?.text || r.status}
                          </Badge>
                          {r.status === "completed" ? (
                            <Badge
                              className="bg-emerald-700 hover:bg-emerald-700 text-white border-emerald-800"
                              data-testid={`route-delivered-badge-${r.id}`}
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Delivered · {r.progress_done ?? 0}/{r.progress_total ?? 0} stops
                            </Badge>
                          ) : r.is_fully_packed && (
                            <Badge
                              className="bg-emerald-600 hover:bg-emerald-600 text-white border-emerald-700"
                              data-testid={`route-packed-badge-${r.id}`}
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" />Packed · Ready for delivery
                            </Badge>
                          )}
                          {r.delivery_date && (
                            <Badge variant="outline" className="text-xs">
                              <Calendar className="w-3 h-3 mr-0.5" />{r.delivery_date}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground mt-1">
                          <span><WarehouseIcon className="inline w-3 h-3 mr-0.5" />{startName || "—"}</span>
                          <ChevronRight className="w-3 h-3" />
                          <span><MapPin className="inline w-3 h-3 mr-0.5" />{(r.order_ids || []).length + (r.custom_stops || []).length} stops</span>
                          <ChevronRight className="w-3 h-3" />
                          <span><WarehouseIcon className="inline w-3 h-3 mr-0.5" />{endName || "—"}</span>
                          {r.total_distance_m != null && (
                            <span className="text-blue-700">· {fmtMeters(r.total_distance_m)}</span>
                          )}
                          {(() => {
                            const stopsN = (r.order_ids || []).length + (r.custom_stops || []).length;
                            const dwellS = stopsN * ((r.dwell_minutes_per_stop || 30) * 60);
                            if (r.total_duration_s == null && stopsN === 0) return null;
                            return (
                              <span
                                className="text-blue-700"
                                title={`Driving ${fmtSeconds(r.total_duration_s || 0)} + ${stopsN} stop(s) × ${r.dwell_minutes_per_stop || 30} min`}
                                data-testid={`route-total-time-${r.id}`}
                              >
                                <Clock className="inline w-3 h-3 mr-0.5" />{fmtSeconds((r.total_duration_s || 0) + dwellS)}
                              </span>
                            );
                          })()}
                        </div>
                        {(r.driver || r.vehicle) && (
                          <div className="flex items-center gap-2 flex-wrap mt-1.5">
                            {r.driver && (
                              <Badge variant="outline" className="text-neutral-700 border-neutral-300 text-xs" data-testid={`route-driver-${r.id}`}>
                                <User className="w-3 h-3 mr-1" />{r.driver}
                              </Badge>
                            )}
                            {r.vehicle && (
                              <Badge variant="outline" className="text-neutral-700 border-neutral-300 text-xs" data-testid={`route-vehicle-${r.id}`}>
                                <Car className="w-3 h-3 mr-1" />{r.vehicle}
                              </Badge>
                            )}
                          </div>
                        )}
                        {r.progress_total > 0 && (
                          <div className="mt-2" data-testid={`route-progress-${r.id}`}>
                            <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                              <span>Live progress</span>
                              <span className="font-mono">{r.progress_done}/{r.progress_total}</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-neutral-200 overflow-hidden">
                              <div
                                className={`h-full ${r.progress_done === r.progress_total ? "bg-emerald-500" : "bg-brand-navy"} transition-all`}
                                style={{ width: `${(r.progress_done / r.progress_total) * 100}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 flex-shrink-0 flex-wrap">
                        {r.status !== "completed" && (
                          <Button
                            size="sm"
                            onClick={() => setPackRunId(r.id)}
                            data-testid={`route-pack-${r.id}`}
                            className={r.is_fully_packed
                              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                              : "bg-brand-navy hover:bg-brand-navy-hover text-white"}
                          >
                            <PackageOpen className="w-3.5 h-3.5 mr-1" />
                            {r.is_fully_packed ? "Pack ✓" : "Pack"}
                          </Button>
                        )}
                        {r.status !== "completed" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPickingRun(r)}
                            data-testid={`route-picking-summary-${r.id}`}
                            className="border-brand-navy text-brand-navy hover:bg-brand-navy/5"
                          >
                            <PackageOpen className="w-3.5 h-3.5 mr-1" />Picking
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => { setEditingRoute(r); setEditorOpen(true); }} data-testid={`route-edit-${r.id}`}>
                          <Edit2 className="w-3.5 h-3.5 mr-1" />Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setLiveViewRun(r)}
                          data-testid={`route-live-${r.id}`}
                          className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" />Live
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => printRouteSheet(r.id, warehouses, "label")}
                          data-testid={`route-print-${r.id}`}
                          className="text-brand-navy border-brand-navy/40 hover:bg-brand-navy/5"
                        >
                          <Printer className="w-3.5 h-3.5 mr-1" />Sheet
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => printRouteSheet(r.id, warehouses, "a4")}
                          data-testid={`route-print-a4-${r.id}`}
                          className="text-brand-navy border-brand-navy/40 hover:bg-brand-navy/5"
                        >
                          <Printer className="w-3.5 h-3.5 mr-1" />Sheet A4
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => setConfirmDelete(r)} data-testid={`route-delete-${r.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      <RouteEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        route={editingRoute}
        warehouses={warehouses}
        onSaved={load}
      />

      {/* ── Packed — ready to deliver. Desktop fallback for marking an
          order delivered without the PDA bag scan. ── */}
      {packedOrders.length > 0 && (
        <div className="pt-2" data-testid="packed-ready-section">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
            Packed · ready to deliver · {packedOrders.length}
          </h3>
          <div className="border rounded-md bg-white overflow-hidden divide-y">
            {packedOrders.map((o) => {
              const bp = o.bag_progress || {};
              return (
                <div
                  key={o.order_id}
                  data-testid={`packed-ready-${o.order_id}`}
                  className="flex items-center gap-3 px-3 py-2 text-sm bg-emerald-50/30 flex-wrap"
                >
                  <PackageOpen className="w-4 h-4 text-emerald-700 shrink-0" />
                  <span className="font-mono text-xs font-semibold shrink-0">{o.order_number}</span>
                  <span className="truncate flex-1 min-w-[120px]">{o.hotel_name}</span>
                  {o.delivery_date && <span className="text-xs text-muted-foreground shrink-0">{o.delivery_date}</span>}
                  {o.run_name && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-sky-50 text-sky-700 border-sky-200 shrink-0">
                      {o.run_name}
                    </span>
                  )}
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-green-50 text-green-700 border-green-200 shrink-0">
                    {bp.packed || 0} bag{(bp.packed || 0) === 1 ? "" : "s"} packed{bp.delivered ? ` · ${bp.delivered} delivered` : ""}
                  </span>
                  <Button
                    size="sm"
                    onClick={() => setConfirmDeliver(o)}
                    data-testid={`desktop-deliver-${o.order_id}`}
                    className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2 shrink-0"
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Mark delivered
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Unassigned orders — approved but not yet on any active run.
          Grouped by delivery_date + shows the hotel's assigned service
          route on each row so planners can spot which run each order
          should join. ── */}
      <div className="pt-2" data-testid="unassigned-orders-section">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Unassigned orders · {unassigned.length}
          </h3>
          {/* Pickup multi-select action bar — appears only when at least
              one pickup order checkbox is ticked. */}
          {selectedPickupIds.size > 0 && (
            <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-md px-3 py-1.5" data-testid="pickup-combined-toolbar">
              <span className="text-xs font-semibold text-purple-800">
                {selectedPickupIds.size} pickup{selectedPickupIds.size === 1 ? "" : "s"} selected
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-100"
                onClick={() => setSelectedPickupIds(new Set())}
                data-testid="pickup-combined-clear"
              >
                Clear
              </Button>
              <Button
                size="sm"
                className="h-7 bg-purple-600 hover:bg-purple-700 text-white text-xs px-3"
                onClick={() => setPickupCombinedOpen(true)}
                data-testid="pickup-combined-open"
              >
                <Printer className="w-3 h-3 mr-1" />
                Combined packing list
              </Button>
            </div>
          )}
        </div>
        {unassigned.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Every approved order is on a run. New approved orders will appear here.
          </p>
        ) : (
          <div className="space-y-3">
            {(() => {
              // date -> Map(routeLabel -> orders[])
              const byDate = new Map();
              for (const o of unassigned) {
                const d = o.delivery_date || "No date";
                const r = o.route_name || (o.route_id ? o.route_id : "No route");
                if (!byDate.has(d)) byDate.set(d, new Map());
                const routes = byDate.get(d);
                if (!routes.has(r)) routes.set(r, []);
                routes.get(r).push(o);
              }
              const dateGroups = Array.from(byDate.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([d, routes]) => [
                  d,
                  Array.from(routes.entries()).sort(([a], [b]) => a.localeCompare(b)),
                ]);
              return dateGroups.map(([date, routeGroups]) => {
                const dateCount = routeGroups.reduce((s, [, list]) => s + list.length, 0);
                return (
                  <div key={date} className="border rounded-md bg-neutral-50 overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-100 border-b">
                      <Calendar className="w-3.5 h-3.5 text-brand-navy" />
                      <span className="font-bold text-sm text-brand-navy">{date}</span>
                      <span className="text-xs text-muted-foreground">· {dateCount}</span>
                    </div>
                    <div>
                      {routeGroups.map(([routeLabel, list]) => (
                        <div key={routeLabel} className="border-b last:border-b-0">
                          <div className="flex items-center gap-2 px-3 py-1 pl-6 bg-white border-b">
                            <RouteIcon className="w-3 h-3 text-neutral-500" />
                            <span className="text-xs font-semibold text-neutral-700">{routeLabel}</span>
                            <span className="text-[10px] text-muted-foreground">· {list.length}</span>
                          </div>
                          <div className="divide-y">
                            {list.map((o) => {
                              const pcs = (o.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
                              const fullyPacked = pcs > 0 && (o.packed_pcs || 0) >= pcs;
                              return (
                                <div
                                  key={o.order_id}
                                  data-testid={`unassigned-order-${o.order_id}`}
                                  className="flex items-center gap-3 px-3 py-2 pl-10 text-sm bg-white"
                                >
                                  {o.is_pickup ? (
                                    <input
                                      type="checkbox"
                                      className="w-4 h-4 shrink-0 accent-purple-600 cursor-pointer"
                                      checked={selectedPickupIds.has(o.order_id)}
                                      onChange={(e) => {
                                        setSelectedPickupIds((prev) => {
                                          const next = new Set(prev);
                                          if (e.target.checked) next.add(o.order_id);
                                          else next.delete(o.order_id);
                                          return next;
                                        });
                                      }}
                                      data-testid={`unassigned-pickup-checkbox-${o.order_id}`}
                                      title="Include in combined packing list"
                                    />
                                  ) : (
                                    // Keep alignment consistent with pickup rows even when there's no checkbox.
                                    <span className="w-4 shrink-0" aria-hidden="true" />
                                  )}
                                  <span className="font-mono text-xs font-semibold shrink-0">{o.order_number}</span>
                                  <span className="truncate flex-1">{o.hotel_name}</span>
                                  <span className="text-xs text-muted-foreground shrink-0">{pcs} pcs</span>
                                  {o.is_pickup ? (
                                    <>
                                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${
                                        fullyPacked
                                          ? "bg-green-50 text-green-700 border-green-200"
                                          : "bg-amber-50 text-amber-700 border-amber-200"
                                      }`}>
                                        {fullyPacked ? "Packed" : "Not packed"}
                                      </span>
                                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-purple-50 text-purple-700 border-purple-200 shrink-0">
                                        Pickup
                                      </span>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => openPickupPack(o)}
                                        data-testid={`unassigned-pack-${o.order_id}`}
                                        className={`h-7 text-xs px-2 shrink-0 ${
                                          fullyPacked
                                            ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                            : "border-purple-300 text-purple-700 hover:bg-purple-50"
                                        }`}
                                      >
                                        <PackageOpen className="w-3 h-3 mr-1" />
                                        {fullyPacked ? "Pack ✓" : "Pack"}
                                      </Button>
                                      <Button
                                        size="sm"
                                        onClick={() => markPickedUp(o)}
                                        data-testid={`unassigned-mark-picked-up-${o.order_id}`}
                                        className="h-7 bg-purple-600 hover:bg-purple-700 text-white text-xs px-2 shrink-0"
                                      >
                                        <CheckCircle2 className="w-3 h-3 mr-1" />
                                        Mark picked up
                                      </Button>
                                    </>
                                  ) : (
                                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${
                                      fullyPacked
                                        ? "bg-green-50 text-green-700 border-green-200"
                                        : "bg-amber-50 text-amber-700 border-amber-200"
                                    }`}>
                                      {fullyPacked ? "Packed" : "Not packed"}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
            <p className="text-[11px] text-muted-foreground">
              Add these to a run via <strong>New run</strong> (or Edit an existing run) — packing happens per run.
            </p>
          </div>
        )}
      </div>

      {/* ── Recent delivered (last 20) — dispatched orders, both regular
          runs and marked-picked-up pickups. Collapsed by default. */}
      {completedPickups.length > 0 && (
        <div className="pt-4" data-testid="recent-delivered-section">
          <button
            type="button"
            onClick={() => setRecentExpanded((v) => !v)}
            data-testid="recent-delivered-toggle"
            aria-expanded={recentExpanded}
            className="w-full flex items-center gap-2 mb-2 group"
          >
            <ChevronRight
              className={`h-4 w-4 text-muted-foreground transition-transform ${recentExpanded ? "rotate-90" : ""}`}
            />
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground group-hover:text-brand-navy">
              Recent delivered · {completedPickups.length}
            </h3>
          </button>
          {recentExpanded && (
            <div className="border rounded-md bg-white overflow-hidden">
              <div className="divide-y">
                {completedPickups.map((o) => {
                  const pcs = (o.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
                  const when = (o.dispatched_at || o.updated_at || "").slice(0, 16).replace("T", " ");
                  const isPickup = !!o.is_pickup;
                  return (
                    <div
                      key={o.id}
                      data-testid={`recent-delivered-${o.id}`}
                      className={`flex items-center gap-3 px-3 py-2 text-sm ${isPickup ? "bg-purple-50/40" : "bg-emerald-50/40"}`}
                    >
                      <CheckCircle2 className={`w-4 h-4 shrink-0 ${isPickup ? "text-purple-600" : "text-emerald-600"}`} />
                      <span className="font-mono text-xs font-semibold shrink-0">{o.order_number}</span>
                      <span className="truncate flex-1">{o.hotel_name || o.user_email}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{pcs} pcs</span>
                      <span className="text-[10px] font-mono text-neutral-500 shrink-0">{when || "—"}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${
                        isPickup
                          ? "bg-purple-100 text-purple-800 border-purple-300"
                          : "bg-emerald-100 text-emerald-800 border-emerald-300"
                      }`}>
                        {isPickup ? "Picked up" : "Delivered"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <LiveViewModal run={liveViewRun} onClose={() => setLiveViewRun(null)} />
      <PickingSummaryModal run={pickingRun} onClose={() => setPickingRun(null)} />

      <CombinedPickupPackingListModal
        open={pickupCombinedOpen}
        onOpenChange={(v) => setPickupCombinedOpen(v)}
        orders={unassigned.filter((o) => o.is_pickup && selectedPickupIds.has(o.order_id))}
      />

      {/* Standalone Pack modal for pickup orders (not on any run) */}
      {pickupPackDetail && (
        <PackStopModal
          initialDetail={pickupPackDetail}
          hotels={pickupHotels}
          products={pickupProducts}
          onClose={() => { setPickupPackDetail(null); load(); }}
        />
      )}

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="route-delete-confirm-dialog">          <AlertDialogHeader>
            <AlertDialogTitle>Delete delivery run?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete ? (
                <>You&apos;re about to delete <strong>{confirmDelete.name || `Run ${confirmDelete.id.slice(0, 6)}`}</strong>. The orders in this run will remain unchanged — only the run record is removed. This cannot be undone.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="route-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove(confirmDelete)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="route-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDeliver !== null} onOpenChange={(o) => { if (!o) setConfirmDeliver(null); }}>
        <AlertDialogContent data-testid="desktop-deliver-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark order as delivered?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDeliver ? (
                <>
                  <strong>{confirmDeliver.order_number}</strong> — {confirmDeliver.hotel_name}.
                  All {(confirmDeliver.bag_progress?.packed || 0)} packed bag(s) will be marked delivered
                  and the linen moves to the hotel, exactly like a PDA delivery (no bag scan).
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="desktop-deliver-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => desktopDeliver(confirmDeliver)}
              disabled={delivering}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="desktop-deliver-confirm"
            >
              {delivering ? "Delivering…" : "Mark delivered"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delivery history — only on the outer Runs view (hidden on the Pack board) */}
      <DispatchHistory />
    </div>
  );
}
